import React, { useEffect, useMemo, useState } from 'react';
import { canAccess } from '../utils/canAccess';
import Select from 'react-select';
import * as XLSX from 'xlsx';

import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Collapse,
  Form,
  Modal,
  Row,
  Spinner
} from 'react-bootstrap';

import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import filterFactory, { textFilter } from 'react-bootstrap-table2-filter';

import {
  FaGasPump,
  FaPlus,
  FaEdit,
  FaTrash,
  FaHistory,
  FaSyncAlt,
  FaFilter,
  FaFileExcel,
  FaChevronDown,
  FaChevronUp,
  FaWarehouse,
  FaSearch,
  FaTint,
  FaPaperclip,
  FaCheckCircle,
  FaExclamationTriangle,
  FaTimesCircle,
  FaBoxes,
  FaExchangeAlt
} from 'react-icons/fa';

import api from '../services/api';
import TransfertGasoilModal from '../components/TransfertGasoilModal';
import documentService from '../services/documentService';
import DocumentsModal from '../components/DocumentsModal';

// 🎨 Thème ShemsPaie
const COLORS = {
  bg: '#f5f5db',
  brown: '#6d4e36',
  brownLight: '#8b5e3c',
  cream: '#fcfbf7',
  border: '#d8c9b8',
  successSoft: '#e8f5e9',
  warningSoft: '#fff3cd',
  dangerSoft: '#fdecec',
  infoSoft: '#eef6ff'
};

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 42,
    borderRadius: 10,
    borderColor: state.isFocused ? COLORS.brownLight : COLORS.border,
    boxShadow: state.isFocused ? '0 0 0 0.15rem rgba(109, 78, 54, 0.12)' : 'none',
    ':hover': { borderColor: COLORS.brownLight },
    backgroundColor: '#fff'
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 99999
  }),
  menu: (base) => ({
    ...base,
    zIndex: 99999,
    borderRadius: 10,
    overflow: 'hidden'
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? COLORS.brown
      : state.isFocused
      ? COLORS.bg
      : '#fff',
    color: state.isSelected ? '#fff' : COLORS.brown,
    cursor: 'pointer'
  }),
  placeholder: (base) => ({
    ...base,
    color: '#8b6f58'
  }),
  singleValue: (base) => ({
    ...base,
    color: COLORS.brown,
    fontWeight: 500
  })
};

const num = (v) => {
  const x = Number(String(v ?? '').replace(',', '.'));
  return Number.isNaN(x) ? 0 : x;
};

const CiterneGasoilPage = ({ user }) => {
  const canManage = user?.role !== 'pointeur' && canAccess(user, 'citernes-gasoil:write');
  const isAdmin   = user?.role !== 'pointeur' && canAccess(user, 'citernes-gasoil:delete');

  const [chantiers, setChantiers] = useState([]);
  const [citernes, setCiternes] = useState([]);
  const [docCounts, setDocCounts] = useState({});

  const [selectedChantier, setSelectedChantier] = useState(null);
  const [globalSearch, setGlobalSearch] = useState('');
  const [kpiFilter, setKpiFilter] = useState('ALL');

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [err, setErr] = useState('');
  const [success, setSuccess] = useState('');

  const [showAddEdit, setShowAddEdit] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [current, setCurrent] = useState(null);

  const [showDelete, setShowDelete] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const [showAlimenter, setShowAlimenter] = useState(false);
  const [toAlimenter, setToAlimenter] = useState(null);
  const [alimenterLitres, setAlimenterLitres] = useState('');
  const [alimenterCommentaire, setAlimenterCommentaire] = useState('');

  const [showMouvements, setShowMouvements] = useState(false);
  const [toMouvements, setToMouvements] = useState(null);
  const [mouvements, setMouvements] = useState([]);
  const [loadingMouvements, setLoadingMouvements] = useState(false);
  const [showMvtDetails, setShowMvtDetails] = useState(true);

  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showDocsModal, setShowDocsModal] = useState(false);
  const [selectedDocItem, setSelectedDocItem] = useState(null);

  const [form, setForm] = useState({
    chantier_id: '',
    nom: '',
    reference: '',
    capacite_max_l: 0,
    capacite_min_l: 0,
    stock_reel_l: 0,
    emplacement: '',
    actif: true,
    visible_rapport: true
  });

  const resetMessages = () => {
    setErr('');
    setSuccess('');
  };

  const loadChantiers = async () => {
    try {
      const res = await api.get('/chantiers');
      setChantiers(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error(e);
      setErr("❌ Erreur lors du chargement des chantiers.");
    }
  };

  const loadCiternes = async (chantierId = null) => {
    setLoading(true);
    resetMessages();

    try {
      const res = chantierId
        ? await api.get(`/citernes-gasoil/chantier/${chantierId}`)
        : await api.get('/citernes-gasoil');

      const rows = Array.isArray(res.data) ? res.data : [];
      setCiternes(rows);

      if (rows.length) {
        const statsRes = await documentService.getDocumentsStats(
          'citerne_gasoil',
          rows.map((r) => r.id)
        );
        setDocCounts(statsRes?.data || {});
      } else {
        setDocCounts({});
      }
    } catch (e) {
      console.error(e);
      setErr("❌ Impossible de charger les citernes.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadChantiers();
    loadCiternes(null);
  }, []);

  const chantierOptions = useMemo(() => {
    return (chantiers || [])
      .filter((c) => c?.id)
      .map((c) => ({
        value: c.id,
        label: c.name || c.nom || `Chantier #${c.id}`,
        raw: c
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' }));
  }, [chantiers]);

  const selectedFormChantier =
    chantierOptions.find((opt) => String(opt.value) === String(form.chantier_id)) || null;

  const handleFilterChantier = async (opt) => {
    setSelectedChantier(opt);
    await loadCiternes(opt?.value || null);
  };

  const citernesEnhanced = useMemo(() => {
    return (citernes || []).map((row) => {
      const stock = num(row.stock_reel_l);
      const min = num(row.capacite_min_l);
      const max = num(row.capacite_max_l);
      const lowStock = min > 0 && stock < min;

      return {
        ...row,
        documents_count: Number(docCounts[row.id] || 0),
        low_stock: lowStock,
        stock_percent: max > 0 ? Number(((stock / max) * 100).toFixed(1)) : 0,
        chantier_name: row?.chantier?.name || row?.chantier?.nom || `#${row?.chantier_id || ''}`
      };
    });
  }, [citernes, docCounts]);

  const kpi = useMemo(() => {
    const total = citernesEnhanced.length;
    const actives = citernesEnhanced.filter((c) => !!c.actif).length;
    const inactives = citernesEnhanced.filter((c) => !c.actif).length;
    const lowStock = citernesEnhanced.filter((c) => !!c.low_stock).length;
    const withDocs = citernesEnhanced.filter((c) => Number(c.documents_count || 0) > 0).length;
    const stockTotal = citernesEnhanced.reduce((sum, c) => sum + num(c.stock_reel_l), 0);
    const capaciteTotale = citernesEnhanced.reduce((sum, c) => sum + num(c.capacite_max_l), 0);

    return {
      total,
      actives,
      inactives,
      lowStock,
      withDocs,
      stockTotal,
      capaciteTotale
    };
  }, [citernesEnhanced]);

  const filteredCiternes = useMemo(() => {
    const q = String(globalSearch || '').trim().toLowerCase();

    return (citernesEnhanced || []).filter((row) => {
      if (kpiFilter === 'ACTIVE' && !row.actif) return false;
      if (kpiFilter === 'INACTIVE' && row.actif) return false;
      if (kpiFilter === 'LOW' && !row.low_stock) return false;
      if (kpiFilter === 'DOCS' && Number(row.documents_count || 0) <= 0) return false;

      if (!q) return true;

      const hay = [
        row.id,
        row.nom,
        row.reference,
        row.chantier_name,
        row.emplacement,
        row.stock_reel_l,
        row.capacite_min_l,
        row.capacite_max_l,
        row.documents_count,
        row.actif ? 'oui active' : 'non inactive'
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return hay.includes(q);
    });
  }, [citernesEnhanced, globalSearch, kpiFilter]);

  const openDocsModal = (row) => {
    setSelectedDocItem(row);
    setShowDocsModal(true);
  };

  const closeDocsModal = () => {
    setShowDocsModal(false);
    setSelectedDocItem(null);
    loadCiternes(selectedChantier?.value || null);
  };

  const openAdd = () => {
    if (!canManage) return;
    resetMessages();
    setEditMode(false);
    setCurrent(null);

    setForm({
      chantier_id: selectedChantier?.value || '',
      nom: '',
      reference: '',
      capacite_max_l: 0,
      capacite_min_l: 0,
      stock_reel_l: 0,
      emplacement: '',
      actif: true,
      visible_rapport: true
    });

    setShowAddEdit(true);
  };

  const openEdit = (row) => {
    if (!canManage) return;
    resetMessages();
    setEditMode(true);
    setCurrent(row);

    setForm({
      chantier_id: row?.chantier_id || '',
      nom: row?.nom || '',
      reference: row?.reference || '',
      capacite_max_l: num(row?.capacite_max_l),
      capacite_min_l: num(row?.capacite_min_l),
      stock_reel_l: num(row?.stock_reel_l),
      emplacement: row?.emplacement || '',
      actif: row?.actif !== undefined ? !!row.actif : true,
      visible_rapport: row?.visible_rapport !== undefined ? !!row.visible_rapport : true
    });

    setShowAddEdit(true);
  };

  const validateForm = () => {
    if (!form.chantier_id) return "❗ Choisis un chantier.";
    if (!String(form.nom || '').trim()) return "❗ Le nom de la citerne est obligatoire.";

    const max = num(form.capacite_max_l);
    const min = num(form.capacite_min_l);
    const stock = num(form.stock_reel_l);

    if (max < 0 || min < 0 || stock < 0) return "❗ Les valeurs (L) doivent être ≥ 0.";
    if (max > 0 && stock > max) return `⛔ Stock réel (${stock} L) ne peut pas dépasser capacité max (${max} L).`;
    if (max > 0 && min > max) return `⛔ Capacité min (${min} L) ne peut pas dépasser capacité max (${max} L).`;

    return null;
  };

  const saveCiterne = async () => {
    if (!canManage) return;

    resetMessages();
    const v = validateForm();
    if (v) {
      setErr(v);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        capacite_max_l: num(form.capacite_max_l),
        capacite_min_l: num(form.capacite_min_l),
        stock_reel_l: num(form.stock_reel_l)
      };

      if (editMode && current?.id) {
        await api.put(`/citernes-gasoil/${current.id}`, payload);
        setSuccess("✅ Citerne modifiée avec succès.");
      } else {
        await api.post('/citernes-gasoil', payload);
        setSuccess("✅ Citerne ajoutée avec succès.");
      }

      setShowAddEdit(false);
      await loadCiternes(selectedChantier?.value || null);
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "❌ Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  // Bascule rapide depuis le tableau : citerne visible / masquée dans le dashboard (/dashboard)
  const toggleVisibleRapport = async (row) => {
    if (!canManage || !row?.id) return;
    resetMessages();
    try {
      await api.put(`/citernes-gasoil/${row.id}`, { visible_rapport: !row.visible_rapport });
      setSuccess(row.visible_rapport ? "✅ Citerne masquée du dashboard." : "✅ Citerne visible dans le dashboard.");
      await loadCiternes(selectedChantier?.value || null);
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "❌ Erreur lors de la modification.");
    }
  };

  const openDelete = (row) => {
    if (!isAdmin) return;
    resetMessages();
    setToDelete(row);
    setShowDelete(true);
  };

  const confirmDelete = async () => {
    if (!isAdmin || !toDelete?.id) return;

    setSaving(true);
    resetMessages();
    try {
      await api.delete(`/citernes-gasoil/${toDelete.id}`);
      setSuccess("✅ Citerne supprimée.");
      setShowDelete(false);
      setToDelete(null);
      await loadCiternes(selectedChantier?.value || null);
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "❌ Erreur lors de la suppression.");
    } finally {
      setSaving(false);
    }
  };

  const openAlimenter = (row) => {
    if (!canManage) return;
    resetMessages();
    setToAlimenter(row);
    setAlimenterLitres('');
    setAlimenterCommentaire('');
    setShowAlimenter(true);
  };

  const submitAlimenter = async () => {
    if (!canManage || !toAlimenter?.id) return;

    resetMessages();

    const litres = Number(String(alimenterLitres || '').replace(',', '.'));
    if (!litres || Number.isNaN(litres) || litres === 0) {
      setErr("❗ Le champ litres doit être un nombre ≠ 0 (positif=ENTREE, négatif=AJUSTEMENT).");
      return;
    }

    setSaving(true);
    try {
      const body = { litres, commentaire: alimenterCommentaire || null };
      const res = await api.post(`/citernes-gasoil/${toAlimenter.id}/alimenter`, body);

      setSuccess(res?.data?.message || "✅ Mouvement enregistré.");
      setShowAlimenter(false);
      setToAlimenter(null);

      await loadCiternes(selectedChantier?.value || null);
    } catch (e) {
      console.error(e);
      setErr(e?.response?.data?.message || "❌ Erreur lors de l'alimentation/ajustement.");
    } finally {
      setSaving(false);
    }
  };

  const getMvtVariant = (type) => {
    if (type === 'ENTREE') return 'success';
    if (type === 'POINTAGE') return 'primary';
    if (type === 'AJUSTEMENT') return 'warning';
    if (type === 'SORTIE') return 'danger';
    return 'secondary';
  };

  const openMouvements = async (row) => {
    resetMessages();
    setToMouvements(row);
    setShowMouvements(true);
    setLoadingMouvements(true);
    setMouvements([]);

    try {
      const res = await api.get(`/citernes-gasoil/${row.id}/mouvements`);
      setMouvements(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error(e);
      setErr("❌ Impossible de charger l'historique des mouvements.");
    } finally {
      setLoadingMouvements(false);
    }
  };

  const exportMouvementsExcel = () => {
    try {
      if (!mouvements?.length) return;

      const citerneName = (toMouvements?.nom || 'citerne').toString().replace(/[\\/:*?"<>|]/g, '_');
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      const filename = `Historique_Mouvements_${citerneName}_${stamp}.xlsx`;

      const rows = mouvements.map((m) => ({
        ID: m.id,
        Date: m.date_mouvement ? new Date(m.date_mouvement).toLocaleString() : '',
        Type: m.type,
        Litres: Number(m.litres || 0),
        Commentaire: m.commentaire || '',
        'User ID': m.user_id || '',
        'Citerne ID': m.citerne_id || '',
        'Pointage ID': m.pointage_materiel_id || '',
        'Matériel ID': m.id_materiel || '',
        'Matricule Matériel': m.matricule_materiel || '',
        'Type Matériel': m.type_materiel || ''
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Mouvements');
      XLSX.writeFile(wb, filename);
    } catch (e) {
      console.error(e);
      setErr("❌ Erreur export Excel.");
    }
  };

  const getDocsBadgeVariant = (count) => {
    const n = Number(count || 0);
    if (n === 0) return 'secondary';
    if (n <= 3) return 'info';
    if (n <= 6) return 'success';
    return 'warning';
  };

  const stockBadge = (row) => {
    if (row.low_stock) {
      return <Badge bg="warning" text="dark">⚠️ Bas</Badge>;
    }
    return <Badge bg="success">OK</Badge>;
  };

  const columns = useMemo(() => {
    return [
      {
        dataField: 'id',
        text: 'ID',
        sort: true,
        filter: textFilter({
          placeholder: 'ID...',
          className: 'form-control form-control-sm shadow-sm'
        }),
        headerStyle: {
          backgroundColor: COLORS.bg,
          color: COLORS.brown,
          width: '70px'
        }
      },
      {
        dataField: 'chantier_name',
        text: 'Chantier 🏗️',
        sort: true,
        filter: textFilter({
          placeholder: '🔎 Chantier...',
          className: 'form-control form-control-sm shadow-sm'
        }),
        formatter: (_, row) => row.chantier_name,
        headerStyle: {
          backgroundColor: COLORS.bg,
          color: COLORS.brown,
          minWidth: '180px'
        }
      },
      {
        dataField: 'nom',
        text: 'Citerne 🛢️',
        sort: true,
        filter: textFilter({
          placeholder: '🔎 Nom citerne...',
          className: 'form-control form-control-sm shadow-sm'
        }),
        headerStyle: {
          backgroundColor: COLORS.bg,
          color: COLORS.brown,
          minWidth: '170px'
        }
      },
      {
        dataField: 'reference',
        text: 'Réf',
        sort: true,
        filter: textFilter({
          placeholder: '🔎 Référence...',
          className: 'form-control form-control-sm shadow-sm'
        }),
        formatter: (cell) => cell || <span className="text-muted">—</span>,
        headerStyle: {
          backgroundColor: COLORS.bg,
          color: COLORS.brown,
          minWidth: '140px'
        }
      },
      {
        dataField: 'stock_reel_l',
        text: 'Stock (L) ⛽',
        sort: true,
        formatter: (cell, row) => (
          <div>
            <div className="d-flex align-items-center gap-2">
              <strong>{num(row.stock_reel_l)}</strong>
              {stockBadge(row)}
            </div>
            <div className="text-muted" style={{ fontSize: 12 }}>
              Min: {num(row.capacite_min_l)} L {num(row.capacite_max_l) > 0 ? `| Max: ${num(row.capacite_max_l)} L` : ''}
            </div>
          </div>
        ),
        headerStyle: {
          backgroundColor: COLORS.bg,
          color: COLORS.brown,
          minWidth: '185px'
        }
      },
      {
        dataField: 'actif',
        text: 'Actif',
        sort: true,
        filter: textFilter({
          placeholder: 'Oui / Non...',
          className: 'form-control form-control-sm shadow-sm'
        }),
        formatter: (cell) => (!!cell ? <Badge bg="success">Oui</Badge> : <Badge bg="secondary">Non</Badge>),
        filterValue: (row) => (row?.actif ? 'Oui' : 'Non'),
        headerStyle: {
          backgroundColor: COLORS.bg,
          color: COLORS.brown,
          width: '100px'
        }
      },
      {
        dataField: 'visible_rapport',
        text: 'Visible rapport 📊',
        sort: true,
        filter: textFilter({
          placeholder: 'Oui / Non...',
          className: 'form-control form-control-sm shadow-sm'
        }),
        filterValue: (row) => (row?.visible_rapport ? 'Oui' : 'Non'),
        formatter: (cell, row) => (canManage ? (
          <Form.Check
            type="switch"
            id={`visible-rapport-${row.id}`}
            checked={!!cell}
            onChange={() => toggleVisibleRapport(row)}
            title={cell ? 'Visible dans le dashboard — cliquer pour masquer' : 'Masquée du dashboard — cliquer pour afficher'}
          />
        ) : (!!cell ? <Badge bg="success">Oui</Badge> : <Badge bg="secondary">Non</Badge>)),
        headerStyle: {
          backgroundColor: COLORS.bg,
          color: COLORS.brown,
          width: '130px'
        }
      },
      {
        dataField: 'documents_count',
        text: '📎 Docs',
        sort: true,
        filter: textFilter({
          placeholder: 'Docs...',
          className: 'form-control form-control-sm shadow-sm'
        }),
        headerStyle: {
          backgroundColor: COLORS.bg,
          color: COLORS.brown,
          width: '110px',
          textAlign: 'center'
        },
        style: { textAlign: 'center' },
        formatter: (c, row) => (
          <Button
            size="sm"
            variant="outline-primary"
            onClick={() => openDocsModal(row)}
            className="d-flex align-items-center justify-content-center gap-1 px-2"
            title="Documents"
          >
            <FaPaperclip />
            <Badge bg={getDocsBadgeVariant(c)}>{Number(c || 0)}</Badge>
          </Button>
        )
      },
      {
        dataField: 'actions',
        text: 'Actions',
        isDummyField: true,
        formatter: (cell, row) => (
          <div className="d-flex gap-2 flex-wrap">
            <Button
              size="sm"
              variant="outline-secondary"
              title="Historique des mouvements"
              onClick={() => openMouvements(row)}
            >
              <FaHistory />
            </Button>

            <Button
              size="sm"
              variant="outline-primary"
              title="Documents"
              onClick={() => openDocsModal(row)}
            >
              <FaPaperclip />
            </Button>

            {canManage ? (
              <>
                <Button
                  size="sm"
                  variant="outline-primary"
                  title="Modifier"
                  onClick={() => openEdit(row)}
                >
                  <FaEdit />
                </Button>

                <Button
                  size="sm"
                  variant="outline-success"
                  title="Alimenter / Ajuster"
                  onClick={() => openAlimenter(row)}
                >
                  <FaGasPump />
                </Button>
              </>
            ) : null}

            {isAdmin ? (
              <Button
                size="sm"
                variant="outline-danger"
                title="Supprimer"
                onClick={() => openDelete(row)}
              >
                <FaTrash />
              </Button>
            ) : null}
          </div>
        ),
        headerStyle: {
          backgroundColor: COLORS.bg,
          color: COLORS.brown,
          minWidth: '220px'
        }
      }
    ];
  }, [canManage, isAdmin, docCounts, selectedChantier]); // eslint-disable-line react-hooks/exhaustive-deps

  const pagination = paginationFactory({
    sizePerPage: 10,
    hideSizePerPage: false,
    showTotal: true,
    sizePerPageList: [
      { text: '10', value: 10 },
      { text: '25', value: 25 },
      { text: '50', value: 50 }
    ]
  });

  const handleKpiClick = (code) => {
    setKpiFilter((prev) => (prev === code ? 'ALL' : code));
  };

  return (
    <div className="container mt-4">
      <Card
        className="shadow-sm border-0"
        style={{
          borderRadius: '18px',
          overflow: 'hidden'
        }}
      >
        <Card.Header
          className="d-flex justify-content-between align-items-center flex-wrap gap-2"
          style={{
            background: 'linear-gradient(135deg, #6d4e36 0%, #8b5e3c 100%)',
            color: '#fff',
            padding: '1rem 1.25rem'
          }}
        >
          <div>
            <h3 className="mb-1 d-flex align-items-center gap-2">
              <FaWarehouse />
              Citernes Gasoil
            </h3>
            <small style={{ opacity: 0.9 }}>
              Gestion des citernes, stocks, mouvements et documents
            </small>
          </div>

          <div className="d-flex gap-2 flex-wrap">
            <Button
              variant="light"
              size="sm"
              onClick={() => loadCiternes(selectedChantier?.value || null)}
              title="Rafraîchir"
              className="fw-bold"
            >
              <FaSyncAlt className="me-1" />
              Rafraîchir
            </Button>

            {canManage ? (
              <Button
                variant="warning"
                size="sm"
                onClick={openAdd}
                className="fw-bold"
              >
                <FaPlus className="me-1" />
                Ajouter
              </Button>
            ) : null}

            <Button
              size="sm"
              className="fw-bold"
              style={{ backgroundColor: '#17a2b8', borderColor: '#17a2b8', color: '#fff' }}
              onClick={() => setShowTransferModal(true)}
            >
              <FaExchangeAlt className="me-1" />
              Transfert Gasoil
            </Button>
          </div>
        </Card.Header>

        <Card.Body style={{ backgroundColor: COLORS.cream }}>
          {err ? (
            <Alert
              variant="danger"
              className="mt-1 shadow-sm"
              style={{ border: `1px solid ${COLORS.brown}` }}
              dismissible
              onClose={() => setErr('')}
            >
              {err}
            </Alert>
          ) : null}

          {success ? (
            <Alert
              variant="success"
              className="mt-1 shadow-sm"
              style={{ border: `1px solid ${COLORS.brown}` }}
              dismissible
              onClose={() => setSuccess('')}
            >
              {success}
            </Alert>
          ) : null}

          {/* KPI STYLE VIDANGE */}
          <Row className="g-3 mb-3">
            <Col md={2}>
              <Card
                className="shadow-sm border-0 h-100"
                style={{
                  borderRadius: '14px',
                  cursor: 'pointer',
                  backgroundColor: kpiFilter === 'ALL' ? COLORS.bg : '#fff'
                }}
                onClick={() => handleKpiClick('ALL')}
              >
                <Card.Body>
                  <div className="d-flex align-items-center gap-2">
                    <FaWarehouse style={{ color: COLORS.brown }} />
                    <div>
                      <div className="fw-bold" style={{ color: COLORS.brown }}>Total</div>
                      <div style={{ fontSize: '1.4rem', fontWeight: 'bold', color: COLORS.brownLight }}>
                        {kpi.total}
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={2}>
              <Card
                className="shadow-sm border-0 h-100"
                style={{
                  borderRadius: '14px',
                  cursor: 'pointer',
                  backgroundColor: kpiFilter === 'ACTIVE' ? COLORS.successSoft : '#fff'
                }}
                onClick={() => handleKpiClick('ACTIVE')}
              >
                <Card.Body>
                  <div className="d-flex align-items-center gap-2">
                    <FaCheckCircle style={{ color: '#198754' }} />
                    <div>
                      <div className="fw-bold" style={{ color: COLORS.brown }}>Actives</div>
                      <div style={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#198754' }}>
                        {kpi.actives}
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={2}>
              <Card
                className="shadow-sm border-0 h-100"
                style={{
                  borderRadius: '14px',
                  cursor: 'pointer',
                  backgroundColor: kpiFilter === 'INACTIVE' ? COLORS.infoSoft : '#fff'
                }}
                onClick={() => handleKpiClick('INACTIVE')}
              >
                <Card.Body>
                  <div className="d-flex align-items-center gap-2">
                    <FaTimesCircle style={{ color: '#6c757d' }} />
                    <div>
                      <div className="fw-bold" style={{ color: COLORS.brown }}>Inactives</div>
                      <div style={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#6c757d' }}>
                        {kpi.inactives}
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={2}>
              <Card
                className="shadow-sm border-0 h-100"
                style={{
                  borderRadius: '14px',
                  cursor: 'pointer',
                  backgroundColor: kpiFilter === 'LOW' ? COLORS.warningSoft : '#fff'
                }}
                onClick={() => handleKpiClick('LOW')}
              >
                <Card.Body>
                  <div className="d-flex align-items-center gap-2">
                    <FaExclamationTriangle style={{ color: '#b8860b' }} />
                    <div>
                      <div className="fw-bold" style={{ color: COLORS.brown }}>Stock bas</div>
                      <div style={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#b8860b' }}>
                        {kpi.lowStock}
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={2}>
              <Card
                className="shadow-sm border-0 h-100"
                style={{
                  borderRadius: '14px',
                  cursor: 'pointer',
                  backgroundColor: kpiFilter === 'DOCS' ? COLORS.infoSoft : '#fff'
                }}
                onClick={() => handleKpiClick('DOCS')}
              >
                <Card.Body>
                  <div className="d-flex align-items-center gap-2">
                    <FaPaperclip style={{ color: '#0d6efd' }} />
                    <div>
                      <div className="fw-bold" style={{ color: COLORS.brown }}>Avec docs</div>
                      <div style={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#0d6efd' }}>
                        {kpi.withDocs}
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={2}>
              <Card className="shadow-sm border-0 h-100" style={{ borderRadius: '14px' }}>
                <Card.Body>
                  <div className="d-flex align-items-center gap-2">
                    <FaBoxes style={{ color: COLORS.brownLight }} />
                    <div>
                      <div className="fw-bold" style={{ color: COLORS.brown }}>Stock total</div>
                      <div style={{ fontSize: '1.1rem', fontWeight: 'bold', color: COLORS.brownLight }}>
                        {kpi.stockTotal} L
                      </div>
                      <div className="text-muted" style={{ fontSize: 12 }}>
                        Capacité : {kpi.capaciteTotale} L
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="mb-3 shadow-sm border-0" style={{ borderRadius: '14px' }}>
            <Card.Header
              style={{
                background: COLORS.bg,
                borderBottom: `1px solid ${COLORS.border}`,
                color: COLORS.brown
              }}
            >
              <div className="d-flex align-items-center gap-2">
                <FaFilter />
                <strong>Filtres & Recherche</strong>
              </div>
            </Card.Header>

            <Card.Body>
              <Row className="g-3 align-items-end">
                <Col md={5}>
                  <Form.Label className="fw-bold" style={{ color: COLORS.brown }}>
                    Chantier 🏗️
                  </Form.Label>
                  <Select
                    styles={selectStyles}
                    options={chantierOptions}
                    value={selectedChantier}
                    onChange={handleFilterChantier}
                    isClearable
                    isSearchable
                    placeholder="🔎 Écrire pour rechercher un chantier..."
                    noOptionsMessage={() => 'Aucun chantier trouvé'}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                  <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                    <FaSearch className="me-1" />
                    Tu peux taper une partie du nom pour filtrer rapidement 🙂
                  </div>
                </Col>

                <Col md={4}>
                  <Form.Label className="fw-bold" style={{ color: COLORS.brown }}>
                    Recherche globale 🔎
                  </Form.Label>
                  <Form.Control
                    value={globalSearch}
                    onChange={(e) => setGlobalSearch(e.target.value)}
                    placeholder="Nom, référence, chantier, emplacement..."
                  />
                </Col>

                <Col md={3}>
                  <Card
                    className="border-0 shadow-sm h-100"
                    style={{ borderRadius: '12px', backgroundColor: COLORS.bg }}
                  >
                    <Card.Body className="py-3">
                      <div className="d-flex align-items-center gap-2">
                        <FaTint style={{ color: COLORS.brown }} />
                        <div>
                          <div className="fw-bold" style={{ color: COLORS.brown }}>
                            Citernes affichées
                          </div>
                          <div style={{ fontSize: '1.3rem', fontWeight: 'bold', color: COLORS.brownLight }}>
                            {filteredCiternes.length}
                          </div>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Card className="shadow-sm border-0" style={{ borderRadius: '14px' }}>
            <Card.Header
              style={{
                background: COLORS.bg,
                borderBottom: `1px solid ${COLORS.border}`,
                color: COLORS.brown
              }}
            >
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <div>
                  <FaGasPump className="me-2" />
                  <strong>Liste des citernes</strong>
                </div>
                <div className="text-muted" style={{ fontSize: 12 }}>
                  Total : <strong>{filteredCiternes.length}</strong>
                </div>
              </div>
            </Card.Header>

            <Card.Body>
              {loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                  <div className="mt-2 text-muted">Chargement...</div>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <BootstrapTable
                    keyField="id"
                    data={filteredCiternes}
                    columns={columns}
                    bootstrap4
                    striped
                    hover
                    condensed
                    bordered={false}
                    pagination={pagination}
                    filter={filterFactory()}
                    noDataIndication="Aucune citerne trouvée 🙂"
                    rowStyle={() => ({
                      backgroundColor: '#fff',
                      color: '#4e3929'
                    })}
                  />
                </div>
              )}
            </Card.Body>
          </Card>
        </Card.Body>
      </Card>

      <Modal show={showAddEdit} onHide={() => setShowAddEdit(false)} centered size="lg">
        <Modal.Header closeButton style={{ background: COLORS.bg, borderColor: COLORS.brown }}>
          <Modal.Title style={{ color: COLORS.brown }}>
            {editMode ? 'Modifier la citerne ✏️' : 'Ajouter une citerne ➕'}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ backgroundColor: COLORS.cream }}>
          <Row className="g-3">
            <Col md={6}>
              <Form.Label className="fw-bold" style={{ color: COLORS.brown }}>
                Chantier 🏗️
              </Form.Label>
              <Select
                styles={selectStyles}
                options={chantierOptions}
                value={selectedFormChantier}
                onChange={(selected) =>
                  setForm((p) => ({ ...p, chantier_id: selected ? selected.value : '' }))
                }
                isClearable
                isSearchable
                placeholder="🔎 Écrire pour rechercher un chantier..."
                noOptionsMessage={() => 'Aucun chantier trouvé'}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </Col>

            <Col md={6}>
              <Form.Label className="fw-bold" style={{ color: COLORS.brown }}>
                Nom de la citerne 🛢️
              </Form.Label>
              <Form.Control
                value={form.nom}
                onChange={(e) => setForm((p) => ({ ...p, nom: e.target.value }))}
                placeholder="Ex: Citerne principale"
              />
            </Col>

            <Col md={6}>
              <Form.Label className="fw-bold" style={{ color: COLORS.brown }}>
                Référence
              </Form.Label>
              <Form.Control
                value={form.reference}
                onChange={(e) => setForm((p) => ({ ...p, reference: e.target.value }))}
                placeholder="Ex: CIT-001"
              />
            </Col>

            <Col md={6}>
              <Form.Label className="fw-bold" style={{ color: COLORS.brown }}>
                Emplacement
              </Form.Label>
              <Form.Control
                value={form.emplacement}
                onChange={(e) => setForm((p) => ({ ...p, emplacement: e.target.value }))}
                placeholder="Ex: Dépôt Nord"
              />
            </Col>

            <Col md={4}>
              <Form.Label className="fw-bold" style={{ color: COLORS.brown }}>
                Capacité max (L)
              </Form.Label>
              <Form.Control
                type="number"
                value={form.capacite_max_l}
                onChange={(e) => setForm((p) => ({ ...p, capacite_max_l: e.target.value }))}
              />
            </Col>

            <Col md={4}>
              <Form.Label className="fw-bold" style={{ color: COLORS.brown }}>
                Capacité min (L)
              </Form.Label>
              <Form.Control
                type="number"
                value={form.capacite_min_l}
                onChange={(e) => setForm((p) => ({ ...p, capacite_min_l: e.target.value }))}
              />
            </Col>

            <Col md={4}>
              <Form.Label className="fw-bold" style={{ color: COLORS.brown }}>
                Stock réel (L)
              </Form.Label>
              <Form.Control
                type="number"
                value={form.stock_reel_l}
                onChange={(e) => setForm((p) => ({ ...p, stock_reel_l: e.target.value }))}
              />
            </Col>

            <Col md={12}>
              <Form.Check
                type="switch"
                id="citerne-actif-switch"
                label={form.actif ? '✅ Citerne active' : '⚪ Citerne inactive'}
                checked={!!form.actif}
                onChange={(e) => setForm((p) => ({ ...p, actif: e.target.checked }))}
              />
            </Col>

            <Col md={12}>
              <Form.Check
                type="switch"
                id="citerne-visible-rapport-switch"
                label={form.visible_rapport ? '📊 Visible dans le dashboard' : '🙈 Masquée du dashboard'}
                checked={!!form.visible_rapport}
                onChange={(e) => setForm((p) => ({ ...p, visible_rapport: e.target.checked }))}
              />
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer style={{ backgroundColor: COLORS.cream }}>
          <Button variant="secondary" onClick={() => setShowAddEdit(false)}>
            Annuler
          </Button>
          <Button variant="primary" onClick={saveCiterne} disabled={saving}>
            {saving ? <Spinner size="sm" className="me-2" /> : null}
            Enregistrer
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDelete} onHide={() => setShowDelete(false)} centered>
        <Modal.Header closeButton style={{ background: COLORS.bg, borderColor: COLORS.brown }}>
          <Modal.Title style={{ color: COLORS.brown }}>Supprimer la citerne 🗑️</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: COLORS.cream }}>
          Confirmer la suppression de <strong>{toDelete?.nom}</strong> ?
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: COLORS.cream }}>
          <Button variant="secondary" onClick={() => setShowDelete(false)}>
            Annuler
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={saving}>
            {saving ? <Spinner size="sm" className="me-2" /> : null}
            Supprimer
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showAlimenter} onHide={() => setShowAlimenter(false)} centered>
        <Modal.Header closeButton style={{ background: COLORS.bg, borderColor: COLORS.brown }}>
          <Modal.Title style={{ color: COLORS.brown }}>
            Alimenter / Ajuster ⛽
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: COLORS.cream }}>
          <div className="mb-2">
            <strong>Citerne :</strong> {toAlimenter?.nom || '—'}
          </div>

          <Form.Group className="mb-3">
            <Form.Label>Litres (+ entrée / - ajustement)</Form.Label>
            <Form.Control
              type="number"
              value={alimenterLitres}
              onChange={(e) => setAlimenterLitres(e.target.value)}
              placeholder="Ex: 500 ou -20"
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Commentaire</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={alimenterCommentaire}
              onChange={(e) => setAlimenterCommentaire(e.target.value)}
              placeholder="Observation / motif..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: COLORS.cream }}>
          <Button variant="secondary" onClick={() => setShowAlimenter(false)}>
            Annuler
          </Button>
          <Button variant="success" onClick={submitAlimenter} disabled={saving}>
            {saving ? <Spinner size="sm" className="me-2" /> : null}
            Valider
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showMouvements} onHide={() => setShowMouvements(false)} centered size="xl">
        <Modal.Header closeButton style={{ background: COLORS.bg, borderColor: COLORS.brown }}>
          <Modal.Title style={{ color: COLORS.brown }}>
            Historique des mouvements 📜
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ backgroundColor: COLORS.cream }}>
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
            <div>
              <strong>Citerne :</strong> {toMouvements?.nom || '—'}
            </div>

            <div className="d-flex gap-2">
              <Button
                variant="outline-success"
                size="sm"
                onClick={exportMouvementsExcel}
                disabled={!mouvements.length}
              >
                <FaFileExcel className="me-1" />
                Export Excel
              </Button>

              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setShowMvtDetails((p) => !p)}
              >
                {showMvtDetails ? <FaChevronUp className="me-1" /> : <FaChevronDown className="me-1" />}
                {showMvtDetails ? 'Masquer' : 'Afficher'}
              </Button>
            </div>
          </div>

          <Collapse in={showMvtDetails}>
            <div>
              {loadingMouvements ? (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                </div>
              ) : !mouvements.length ? (
                <Alert variant="secondary">Aucun mouvement trouvé.</Alert>
              ) : (
                <div style={{ maxHeight: 450, overflow: 'auto' }}>
                  <table className="table table-sm table-bordered align-middle">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Litres</th>
                        <th>Commentaire</th>
                        <th>Matériel</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mouvements.map((m) => (
                        <tr key={m.id}>
                          <td>{m.id}</td>
                          <td>{m.date_mouvement ? new Date(m.date_mouvement).toLocaleString('fr-FR') : '—'}</td>
                          <td>
                            <Badge bg={getMvtVariant(m.type)}>{m.type}</Badge>
                          </td>
                          <td>{Number(m.litres || 0)}</td>
                          <td>{m.commentaire || '—'}</td>
                          <td>
                            {m.matricule_materiel
                              ? `${m.matricule_materiel}${m.type_materiel ? ` (${m.type_materiel})` : ''}`
                              : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </Collapse>
        </Modal.Body>

        <Modal.Footer style={{ backgroundColor: COLORS.cream }}>
          <Button variant="secondary" onClick={() => setShowMouvements(false)}>
            Fermer
          </Button>
        </Modal.Footer>
      </Modal>

      <DocumentsModal
        show={showDocsModal}
        onHide={closeDocsModal}
        module="citerne_gasoil"
        moduleId={selectedDocItem?.id}
        itemLabel={
          selectedDocItem
            ? `${selectedDocItem.nom || ''}${selectedDocItem.reference ? ` - ${selectedDocItem.reference}` : ''}`
            : ''
        }
        title="Documents de la citerne"
        canDelete={canManage}
      />

      <TransfertGasoilModal
        show={showTransferModal}
        onHide={() => setShowTransferModal(false)}
        sourceCiternes={citernes}
        onSuccess={() => loadCiternes(selectedChantier?.value || null)}
      />
    </div>
  );
};

export default CiterneGasoilPage;